// Derived views of findings for the Digital Footprint: sectors, plan for the week, weekly change, coverage segments.
// Pure functions over domain types; the UI only draws what these return.
import { atLeast, LADDER, PLANS } from './entitlements';
import { isOpen } from './lifecycle';
import { computeScore, impactOf, pointsOff } from './score';
import type { Category, Finding, ItemType, Plan, Severity, SourceId } from './types';

// Map sectors, clockwise from the top.
export const SECTORS: Category[] = ['dark_web', 'money', 'public_profiles', 'location', 'data_brokers'];
export const ITEM_TYPES: ItemType[] = ['email', 'phone', 'card', 'national_id', 'bank_account', 'username', 'name', 'address'];

// Which source scans each sector.
export const SECTOR_SOURCE: Record<Category, SourceId> = {
  dark_web: 'dark_web', money: 'transactions', public_profiles: 'osint', location: 'osint', data_brokers: 'osint',
};

export const isSectorScanned = (plan: Plan, c: Category) => PLANS[plan].entitlements.sources.includes(SECTOR_SOURCE[c]);

// Lowest plan whose sources include this sector's source (for "Not scanned · Complete").
export function planForSector(c: Category): Plan {
  return LADDER.find((p) => p !== 'free_scan' && PLANS[p].entitlements.sources.includes(SECTOR_SOURCE[c])) ?? 'complete';
}

const SEV_RANK: Record<Severity, number> = { critical: 4, high: 3, medium: 2, low: 1 };

export function worstOpenSeverity(findings: Finding[]): Severity | null {
  let worst: Severity | null = null;
  for (const f of findings) if (isOpen(f.status) && (!worst || SEV_RANK[f.severity] > SEV_RANK[worst])) worst = f.severity;
  return worst;
}

// Your plan this week: open findings with the most points to win back; ties by severity, then newest.
export function planThisWeek(findings: Finding[], n = 3): Finding[] {
  return findings
    .filter((f) => isOpen(f.status) && impactOf(f) > 0)
    .sort((a, b) => impactOf(b) - impactOf(a) || SEV_RANK[b.severity] - SEV_RANK[a.severity] || b.detectedAt.localeCompare(a.detectedAt))
    .slice(0, n);
}

// Weekly change: points taken by findings that arrived in the last 7 days (negative = score went down).
export function weeklyDelta(findings: Finding[], now: string): number {
  const since = Date.parse(now) - 7 * 86_400_000;
  const before = findings.filter((f) => Date.parse(f.detectedAt) < since);
  return computeScore(findings).value - computeScore(before).value;
}

// Score as it stood at a moment: findings detected by then, each in the status it had then
// (closed only if resolvedAt is on or before that moment; otherwise still open).
export function scoreAt(findings: Finding[], at: string | number) {
  const t = typeof at === 'number' ? at : Date.parse(at);
  return computeScore(findings
    .filter((f) => Date.parse(f.detectedAt) <= t)
    .map((f) => (f.status === 'resolved' || f.status === 'dismissed') && (!f.resolvedAt || Date.parse(f.resolvedAt) > t) ? { ...f, status: 'needs_action' as const } : f)).value;
}

// Change over the last `days` (30 on the home screen): today's score minus the score `days` ago.
export const scoreDelta = (findings: Finding[], now: string, days = 30) =>
  computeScore(findings).value - scoreAt(findings, Date.parse(now) - days * 86_400_000);

export const isNew = (f: Finding, now: string) => Date.parse(now) - Date.parse(f.detectedAt) < 86_400_000;

// Findings grouped by sector, ordered by date (oldest first, clockwise).
export function bySector(findings: Finding[]): Record<Category, Finding[]> {
  const out = Object.fromEntries(SECTORS.map((c) => [c, [] as Finding[]])) as Record<Category, Finding[]>;
  for (const f of findings) out[f.category].push(f);
  for (const c of SECTORS) out[c].sort((a, b) => (a.occurredAt ?? a.detectedAt).localeCompare(b.occurredAt ?? b.detectedAt) || a.id.localeCompare(b.id));
  return out;
}

export interface CoverageSegment { type: ItemType; state: 'watched' | 'available' | 'locked'; unlockPlan?: Plan }
export function coverageSegments(plan: Plan, watched: ItemType[]): CoverageSegment[] {
  const limits = PLANS[plan].entitlements.itemLimits;
  return ITEM_TYPES.map((type) => {
    if (watched.includes(type)) return { type, state: 'watched' };
    if ((limits[type] ?? 0) > 0) return { type, state: 'available' };
    return { type, state: 'locked', unlockPlan: LADDER.find((p) => atLeast(p, plan) && (PLANS[p].entitlements.itemLimits[type] ?? 0) > 0) };
  });
}

export const pointsOfOpen = (findings: Finding[]) => findings.reduce((a, f) => a + pointsOff(f), 0);

// ---------- Findings list ----------
export type FindingsTab = 'needs_action' | 'in_progress' | 'resolved';
export const tabOf = (f: Finding): FindingsTab =>
  f.status === 'new' || f.status === 'needs_action' ? 'needs_action' : f.status === 'in_progress' ? 'in_progress' : 'resolved';

export function counters(findings: Finding[]) {
  return {
    needs_action: findings.filter((f) => tabOf(f) === 'needs_action').length,
    in_progress: findings.filter((f) => f.status === 'in_progress').length,
    resolved: findings.filter((f) => f.status === 'resolved').length,
    dismissed: findings.filter((f) => f.status === 'dismissed').length,
  };
}

// Newest and most severe first: severity, then date detected.
export const listOrder = (a: Finding, b: Finding) => SEV_RANK[b.severity] - SEV_RANK[a.severity] || b.detectedAt.localeCompare(a.detectedAt);

// Previous / Next on the finding report walks the Findings list as it is drawn: newest day first, then the most
// severe first within a day (status tabs) or plain time order (All activity). `keepId` stays in the sequence after it
// changes tab (resolved on the report), so its neighbours do not move.
export function listSequence<T extends Finding>(findings: T[], tab: FindingsTab | 'all', keepId?: string): T[] {
  const day = (f: Finding) => f.detectedAt.slice(0, 10);
  return findings
    .filter((f) => tab === 'all' || tabOf(f) === tab || f.id === keepId)
    .sort((a, b) => day(b).localeCompare(day(a)) || (tab === 'all' ? b.detectedAt.localeCompare(a.detectedAt) : listOrder(a, b)));
}

export type RecencyGroup = 'week' | 'month' | 'quarter' | 'older';
export const OLD_AFTER_DAYS = 90;
export function recencyOf(f: Finding, now: string): RecencyGroup {
  const days = (Date.parse(now) - Date.parse(f.detectedAt)) / 86_400_000;
  return days < 7 ? 'week' : days < 30 ? 'month' : days < OLD_AFTER_DAYS ? 'quarter' : 'older';
}

// Queue: open findings the user can act on, most points first.
export const queueOrder = (findings: Finding[]) =>
  findings.filter((f) => f.status === 'new' || f.status === 'needs_action').sort((a, b) => impactOf(b) - impactOf(a) || listOrder(a, b));

// ---------- Plan picker ----------
// What each plan would show in full, counted only from findings we actually scanned (never invented counts).
// A finding is visible on a plan when the plan reads its source and watches at least one of its item types.
export function planReach(plan: Plan, findings: Finding[], itemTypeOf: (itemId: string) => ItemType | undefined) {
  const ent = PLANS[plan].entitlements;
  const visible = findings.filter((f) => {
    if (!ent.detailsVisible || !ent.sources.includes(f.source === 'incogni' || f.source === 'coveron' ? 'osint' : f.source)) return false;
    const types = f.watchedItemIds.map(itemTypeOf).filter(Boolean) as ItemType[];
    return types.length === 0 || types.some((t) => (ent.itemLimits[t] ?? 0) > 0);
  });
  const removable = ent.partners.includes('incogni') ? findings.filter((f) => f.category === 'data_brokers' && isOpen(f.status)).length : 0;
  return { visible: visible.length, total: findings.length, removable, sectors: SECTORS.filter((c) => isSectorScanned(plan, c)).length };
}
